import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

export default function EmailVerification() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [verificationState, setVerificationState] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const handleEmailVerification = async () => {
      try {
        // Get the type and token parameters from the URL
        const type = searchParams.get('type');
        const token = searchParams.get('token');
        const email = searchParams.get('email');
        
        if (!type || !token || !email) {
          throw new Error('Missing parameters for email verification');
        }
        
        if (type === 'signup') {
          // Complete the email verification process with Supabase
          const { error } = await supabase.auth.verifyOtp({
            token_hash: token,
            type: 'signup',
            email
          });
          
          if (error) throw error;
          setVerificationState('success');
          
          // Automatically redirect to sign in page after a short delay
          setTimeout(() => {
            navigate('/');
          }, 3000);
        } else {
          throw new Error('Unsupported verification type');
        }
      } catch (error: any) {
        setVerificationState('error');
        setErrorMessage(error.message || 'Verification failed. Please try again.');
        console.error('Verification error:', error);
      }
    };

    handleEmailVerification();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-gray-800 shadow-xl rounded-2xl p-8 border border-[#c9fffc]/10">
        <div className="text-center">
          {verificationState === 'loading' && (
            <>
              <Loader2 className="h-16 w-16 text-[#c9fffc] mx-auto animate-spin" />
              <h2 className="mt-6 text-2xl font-bold text-white">Verifying your email...</h2>
              <p className="mt-2 text-gray-400">Please wait while we confirm your email address.</p>
            </>
          )}
          
          {verificationState === 'success' && (
            <>
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
              <h2 className="mt-6 text-2xl font-bold text-white">Email Verified!</h2>
              <p className="mt-2 text-gray-400">Your email has been successfully verified.</p>
              <p className="mt-4 text-[#c9fffc]">Redirecting you to sign in...</p>
            </>
          )}
          
          {verificationState === 'error' && (
            <>
              <XCircle className="h-16 w-16 text-red-500 mx-auto" />
              <h2 className="mt-6 text-2xl font-bold text-white">Verification Failed</h2>
              <p className="mt-2 text-gray-400">{errorMessage || 'There was a problem verifying your email.'}</p>
              <button
                onClick={() => navigate('/')}
                className="mt-6 w-full flex items-center justify-center px-4 py-3 bg-[#c9fffc] text-gray-900 rounded-xl hover:bg-[#a0fcf9] focus:outline-none focus:ring-2 focus:ring-[#c9fffc] focus:ring-offset-2 focus:ring-offset-gray-900 font-medium shadow-lg shadow-[#c9fffc]/10 transition-all"
              >
                Back to Sign In
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
